import {tokens, colors, radii} from '@commercial-helios/ui/theme'
import * as EmailValidator from 'email-validator'
import type {CountryCode as PhoneCountryCode} from 'libphonenumber-js'
import {parsePhoneNumberFromString} from 'libphonenumber-js'
import React, {useReducer} from 'react'
import styled, {css} from 'styled-components'

import PhoneInput from '@/components/phone-input'
import {useConsents} from '@/hooks/use-consents'
import {useIsCompanyEmail} from '@/hooks/use-is-company-email'
import useParams from '@/hooks/use-params'
import {arrayShuffle} from '@/utils/array-shuffle'
import {scrollToTop} from '@/utils/scroll'
import Button from '@helios/shared/components/button'
import {Consents} from '@helios/shared/components/consents'
import Input from '@helios/shared/components/input'
import Select from '@helios/shared/components/select'
import useFormErrors from '@helios/shared/hooks/use-form-errors'
import {useGetCountryName} from '@helios/shared/hooks/use-get-country-name'
import useNavigate from '@helios/shared/hooks/use-navigate'
import {useSubmitHubspotForm} from '@helios/shared/hooks/use-submit-hubspot-form'
import type {CountryCode, LanguageCode} from '@helios/shared/locale/locales'
import locales from '@helios/shared/locale/locales'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {useGoogleReCaptcha} from '@helios/shared/providers/google-recaptcha'
import {useLocale} from '@helios/shared/providers/locale'
import {container} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import CompanySize from '@helios/shared/types/company-size'
import type {FormConfig, ImperativeConsent} from '@helios/shared/types/consents'
import DiscoveredVia from '@helios/shared/types/discovered-via'
import type {HubspotContactProps} from '@helios/shared/types/hubspot-contact-props'
import {isBrowser} from '@helios/shared/utils/browser'
import {isGenericEmail} from '@helios/shared/utils/is-generic-email'
import mq from '@helios/shared/utils/media-query'

import * as tracking from '../lead-form/tracking'

// This is the first step to make this form reuseable between other lead forms

export const OTHER = 'Other' as const

export interface State {
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    phoneCountryCode: PhoneCountryCode
    companySize: CompanySize | ''
    country: CountryCode | typeof OTHER | ''
    discoveredVia: DiscoveredVia | ''
    utmCampaign: string
    latestUtmCampaign: string
    hasConsented: boolean
}

enum ActionType {
    SET_VALUE,
}

type SetValue<TKey extends keyof State> = {
    type: ActionType.SET_VALUE
    key: keyof State
    value: State[TKey]
}

type Action<TKey extends keyof State> = SetValue<TKey>

const reducer = <TKey extends keyof State>(state: State, action: Action<TKey>) => {
    switch (action.type) {
        case ActionType.SET_VALUE: {
            return {
                ...state,
                [action.key]: action.value,
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

const useValidateInputs = () => {
    const globalStrings = useGlobalStrings()

    return (state: State, isValidEmail: boolean) => {
        type Error = {
            key: keyof State
            value: string
        }

        const errors: Error[] = []

        const parsedPhoneNumber = parsePhoneNumberFromString(
            state.phoneNumber,
            state.phoneCountryCode
        )
        const isValidPhoneNumber = !!parsedPhoneNumber?.isValid()

        if (!state.firstName.trim()) {
            errors.push({
                key: 'firstName',
                value: globalStrings.first_name_error,
            })
        }

        if (!state.lastName.trim()) {
            errors.push({
                key: 'lastName',
                value: globalStrings.last_name_error,
            })
        }

        if (!state.email.trim() || !isValidEmail || !EmailValidator.validate(state.email)) {
            errors.push({
                key: 'email',
                value: !isValidEmail
                    ? globalStrings.please_enter_your_work_email
                    : globalStrings.invalid_email_error,
            })
        }

        if (!state.phoneNumber.trim() || !isValidPhoneNumber) {
            errors.push({
                key: 'phoneNumber',
                value: globalStrings.phone_number_error,
            })
        }

        if (!state.companySize.trim()) {
            errors.push({
                key: 'companySize',
                value: globalStrings.company_size_error,
            })
        }

        if (!state.country.trim()) {
            errors.push({
                key: 'country',
                value: globalStrings.country_error,
            })
        }

        return errors
    }
}

export enum RedirectType {
    GO_TO_THANK_YOU_SOLE_TRADER,
    GO_TO_CALENDLY,
    GO_TO_THANK_YOU,
}

type Navigate = ReturnType<typeof useNavigate>

const handleRedirectEffect =
    (state: State, navigate: Navigate, language: LanguageCode) => (redirectType: RedirectType) => {
        switch (redirectType) {
            case RedirectType.GO_TO_CALENDLY: {
                if (state.country === 'Other' || state.country === '') {
                    return
                }

                // Makes sure build passes as session storage doesn't exist on build
                if (isBrowser) {
                    const parsedPhoneNumber = parsePhoneNumberFromString(
                        state.phoneNumber,
                        state.phoneCountryCode
                    )?.number

                    sessionStorage.setItem(
                        'calendlyParams',
                        JSON.stringify({
                            source: 'demo-form',
                            name: `${state.firstName} ${state.lastName}`,
                            email: state.email,
                            phoneNumber: parsedPhoneNumber,
                            companySize: state.companySize,
                            country: state.country,
                            language,
                        })
                    )
                }

                return navigate(`/book-a-demo`)
            }

            case RedirectType.GO_TO_THANK_YOU_SOLE_TRADER: {
                return navigate('/thankyousoletrader')
            }

            case RedirectType.GO_TO_THANK_YOU: {
                return navigate('/thankyou')
            }

            default: {
                throw new Error('Unrecognized redirect type')
            }
        }
    }

export const getRedirectType = (companySize: State['companySize'], country: State['country']) => {
    const isOfSizes = (sizes: State['companySize'][]) => sizes.includes(companySize)
    const isOfCountries = (countries: State['country'][]) => countries.includes(country)

    const {SoleTrader} = CompanySize

    if (isOfSizes([SoleTrader]) && !isOfCountries(['dk', 'se'])) {
        return RedirectType.GO_TO_THANK_YOU_SOLE_TRADER
    }

    if (!isOfCountries(['Other'])) {
        return RedirectType.GO_TO_CALENDLY
    }

    return RedirectType.GO_TO_THANK_YOU
}

export interface Props {
    hubspotProperties?: Partial<HubspotContactProps>
    redirectCallback?: () => void
    className?: string
    trackQualifyingLeads?: boolean
    formConfig: FormConfig
    imperativeConsents?: ImperativeConsent[]
    content: {
        preTitle?: string
        title: string
    }
}

export const HubspotForm = ({
    content,
    hubspotProperties,
    redirectCallback,
    className,
    trackQualifyingLeads,
    formConfig,
    imperativeConsents = [],
}: Props) => {
    const currentLocale = useLocale()
    const getCountryName = useGetCountryName()
    const params = useParams()
    const validateInputs = useValidateInputs()
    const {isLoading, recaptchaHubspotSubmit} = useSubmitHubspotForm(
        formConfig.formId || 'dummy',
        false
    )
    const {consentsProps, consentsPayload, validateConsents, ConsentTypes} = useConsents({
        ...formConfig.consents,
        imperativeConsents,
    })

    React.useEffect(() => {
        tracking.bigformReached({country: currentLocale.country, trackQualifyingLeads})
    }, [currentLocale.country, trackQualifyingLeads])

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        // Validate inputs
        const errors = validateInputs(state, isValidEmail)

        if (!validateConsents()) {
            errors.push({
                key: 'hasConsented',
                value: 'Please accept all consents',
            })
        }

        if (errors.length) {
            errors.forEach((error) => {
                setError(error.key, error.value)
            })

            return scrollToTop()
        }

        await recaptchaHubspotSubmit(
            {
                ...state,
                genericEmail: isGenericEmail(state.email),
                ...hubspotProperties,
            },
            consentsPayload
        )

        tracking.bigformSubmitted(state)

        // Redirect user
        if (redirectCallback) {
            redirectCallback()
        } else {
            redirect(getRedirectType(state.companySize, state.country))
        }
    }

    const initialState: State = {
        firstName: '',
        lastName: '',
        email: params.get('email') || '',
        phoneNumber: '',
        phoneCountryCode: currentLocale.country.toUpperCase() as PhoneCountryCode,
        companySize: '',
        country: currentLocale.country,
        discoveredVia: '',
        utmCampaign: params.get('utm_campaign') || '',
        latestUtmCampaign: params.get('latest_utm_campaign') || '',
        hasConsented: false,
    }

    const [state, dispatch] = useReducer(reducer, initialState)
    const {errors, setError, clearError} = useFormErrors(state)
    const isValidEmail = useIsCompanyEmail(state.email || '')
    const navigate = useNavigate()
    const redirect = handleRedirectEffect(state, navigate, currentLocale.language)
    const globalStrings = useGlobalStrings()
    const {loadReCaptcha} = useGoogleReCaptcha()

    const setValue = <TKey extends keyof State>(key: TKey, value: State[TKey]) => {
        dispatch({
            type: ActionType.SET_VALUE,
            key,
            value,
        })
        tracking.bigformInteracted(key)
    }

    const companySizeOptions = Object.values(CompanySize).map((value) => {
        switch (value) {
            case CompanySize.SoleTrader: {
                return {
                    label: globalStrings.sole_trader,
                    value,
                }
            }

            case CompanySize.Size_1: {
                return {
                    label: `${value} ${globalStrings.employee}`,
                    value,
                }
            }

            default: {
                return {
                    label: `${value} ${globalStrings.employees}`,
                    value,
                }
            }
        }
    })

    const uniqueCountryCodes = Array.from(new Set(locales.map((locale) => locale.country)))

    const countryOptions = [
        ...uniqueCountryCodes.map((countryCode) => ({
            label: getCountryName(countryCode),
            value: countryCode,
        })),
        {
            label: globalStrings.other,
            value: OTHER,
        },
    ]

    const discoveredViaOptions = [
        {
            label: globalStrings.peer_recommendation,
            value: DiscoveredVia.PeerRecommendation,
        },
        {
            label: globalStrings.trust_pilot,
            value: DiscoveredVia.TrustpilotG2orCapterra,
        },
        {
            label: globalStrings.google_search,
            value: DiscoveredVia.GoogleSearchEngine,
        },
        {
            label: globalStrings.youtube,
            value: DiscoveredVia.YouTube,
        },
        {
            label: globalStrings.tv,
            value: DiscoveredVia.TV,
        },
        {
            label: globalStrings.outdoor_advertising,
            value: DiscoveredVia.OutdoorAdvertising,
        },
        {
            label: globalStrings.social_media,
            value: DiscoveredVia.SocialMedia,
        },
        {
            label: globalStrings.press,
            value: DiscoveredVia.Press,
        },
    ]

    const discoveredViaOptionsShuffled = arrayShuffle(discoveredViaOptions)

    const handleInputChange: typeof setValue = (key, value) => {
        loadReCaptcha()
        clearError(key)
        setValue(key, value)
    }

    const errorSpacePlaceholder = <Text variant="label-base">{'\u00a0'}</Text>

    return (
        <Form className={className} onSubmit={handleFormSubmit} noValidate>
            <Titles>
                <Text variant="paragraph-base" color="muted" center>
                    {content.preTitle}
                </Text>

                <Text variant="title-base" center>
                    {content.title}
                </Text>
            </Titles>

            <InputsWrapper>
                <Row>
                    <div>
                        <Input
                            label={globalStrings.first_name}
                            value={state.firstName}
                            onChange={(value) => handleInputChange('firstName', value)}
                            error={errors.firstName}
                            required
                            data-testid="hubspot-first-name-input"
                        />

                        {!errors.firstName && errorSpacePlaceholder}
                    </div>

                    <div>
                        <Input
                            label={globalStrings.last_name}
                            value={state.lastName}
                            onChange={(value) => handleInputChange('lastName', value)}
                            error={errors.lastName}
                            required
                            data-testid="hubspot-last-name-input"
                        />

                        {!errors.lastName && errorSpacePlaceholder}
                    </div>
                </Row>

                <Row>
                    <div>
                        <Input
                            type="email"
                            label={globalStrings.work_email}
                            value={state.email}
                            onChange={(value) => handleInputChange('email', value)}
                            error={errors.email}
                            required
                            data-testid="hubspot-email-input"
                        />

                        {!errors.email && errorSpacePlaceholder}
                    </div>

                    <div>
                        <PhoneInput
                            label={globalStrings.phone_number}
                            setPhoneNumber={(value) => handleInputChange('phoneNumber', value)}
                            phoneNumber={state.phoneNumber}
                            setCountryCode={(value) => handleInputChange('phoneCountryCode', value)}
                            countryCode={state.phoneCountryCode}
                            error={errors.phoneNumber}
                            required
                            data-testid="hubspot-phone-number-input"
                        />

                        {!errors.phoneNumber && errorSpacePlaceholder}
                    </div>
                </Row>

                <Row>
                    <div>
                        <Select
                            label={globalStrings.company_size}
                            options={companySizeOptions}
                            placeholder={globalStrings.select}
                            value={state.companySize}
                            onChange={(value) => handleInputChange('companySize', value)}
                            error={errors.companySize}
                            required
                            data-testid="hubspot-company-size-select"
                        />

                        {!errors.companySize && errorSpacePlaceholder}
                    </div>

                    <div>
                        <Select
                            label={globalStrings.country}
                            options={countryOptions}
                            placeholder={globalStrings.country_placeholder}
                            value={state.country}
                            onChange={(value) => handleInputChange('country', value)}
                            error={errors.country}
                            required
                            data-testid="hubspot-country-select"
                        />

                        {!errors.country && errorSpacePlaceholder}
                    </div>
                </Row>
                <Row>
                    <div>
                        <Select
                            label={globalStrings.discovered_via_label}
                            options={discoveredViaOptionsShuffled}
                            placeholder={globalStrings.discovered_via_placeholder}
                            value={state.discoveredVia}
                            onChange={(value) => handleInputChange('discoveredVia', value)}
                            data-testid="hubspot-discovered-via-select"
                        />
                    </div>
                </Row>
            </InputsWrapper>

            {consentsProps.type !== ConsentTypes.None && <Consents {...consentsProps} />}

            <Button loading={isLoading} data-testid="hubspot-submit-button">
                {globalStrings.demo_submit_text}
            </Button>
        </Form>
    )
}

const mobileStylesBreakpoint = 'xs'

const wrapperStyles = css`
    ${container}
    display: grid;
    row-gap: ${tokens.spacing32};
    width: 100%;
    padding: ${tokens.spacing20};
    background: ${colors.background.default};

    ${mq.min(mobileStylesBreakpoint)} {
        max-width: 600px;
        padding: ${tokens.spacing28} ${tokens.spacing40};
        border-radius: ${radii.arc8};
    }
`

const Form = styled.form`
    ${wrapperStyles}
`

const Titles = styled.div`
    display: grid;
    row-gap: ${tokens.spacing10};
`

const InputsWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};
    width: 100%;

    ${mq.min(mobileStylesBreakpoint)} {
        row-gap: ${tokens.spacing12};
    }
`

const Row = styled.div`
    display: grid;
    gap: inherit;
    grid-template-columns: 1fr;

    ${mq.min(mobileStylesBreakpoint)} {
        grid-auto-columns: 1fr;
        grid-auto-flow: column;
    }
`
